import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { userGuard } from './auth-guards/user.guard';
import { portalAdminGuard } from './auth-guards/portal-admin.guard';
import { activationKeysAdminGuard } from './auth-guards/activation-keys-admin.guard';
import { LoginComponent } from '../components/login/login.component';
import { ProductDetailsComponent } from '../components/admin-page/components/product-details/product-details.component';
import { LicensesPageComponent } from '../components/licenses-page/licenses-page.component';
import { TenantDetailsComponent } from '../components/admin-page/components/tenant-details/tenant-details.component';
import { HistoryComponent } from '../components/history/history.component';
import { SignUpComponent } from '../components/sign-up/sign-up.component';
import { TenantUsersComponent } from '../components/tenant-users/tenant-users.component';
import { TenantInfoSetupFormComponent } from '../components/tenant-info-setup-form/tenant-info-setup-form.component';
import { CheckoutPageComponent } from '../components/checkout-page/checkout-page.component';
import { SavedPaymentMethodsComponent } from '../components/saved-payment-methods/saved-payment-methods.component';
import { EditProductComponent } from '../components/admin-page/components/edit-product/edit-product.component';
import { EditProductVariantComponent } from '../components/admin-page/components/edit-product-variant/edit-product-variant.component';
import { AddProductComponent } from '../components/admin-page/components/add-product/add-product.component';
import { AddProductVariantComponent } from '../components/admin-page/components/add-product-variant/add-product-variant.component';
import { ReceiptsPageComponent } from '../components/receipts-page/receipts-page.component';
import { EditLicenseComponent } from '../components/admin-page/components/edit-license/edit-license.component';
import { LicenseChangeHistoryComponent } from '../components/admin-page/components/license-change-history/license-change-history.component';
import { UserSettingsComponent } from '../components/user-settings/user-settings.component';
import { ManageAuthComponent } from '../components/manage-auth/manage-auth.component';
import { DashboardComponent } from '../components/dashboard/dashboard.component';
import { TenantInvoiceComponent } from '../components/admin-page/components/tenant-invoice/tenant-invoice.component';
import { ReceiptPageComponent } from '../components/receipt-page/receipt-page.component';
import { FranchiseDetailsComponent } from '../components/admin-page/components/franchise-details/franchise-details.component';
import { EditUserComponent } from '../components/edit-user/edit-user.component';
import { TermsAndConditionsComponent } from '../components/terms-and-conditions/terms-and-conditions.component';
import { PrivacyPolicyComponent } from '../components/privacy-policy/privacy-policy.component';
import { TenantAdminEditComponent } from '../components/admin-page/components/tenant-admin-edit/tenant-admin-edit.component';
import { TenantInfoPageComponent } from '../components/tenant-info-page/tenant-info-page.component';
import { TenantSettingsComponent } from '../components/tenant-settings/tenant-settings.component';
import { TenantsListComponent } from '../components/admin-page/components/tenants-list/tenants-list.component';
import { ProductListComponent } from '../components/admin-page/components/product-list/product-list.component';
import { CreateTenantComponent } from '../components/admin-page/components/create-tenant/create-tenant.component';
import { FranchisesListComponent } from '../components/admin-page/components/franchises-list/franchises-list.component';
import { KillSwitchComponent } from '../components/admin-page/components/kill-switch/kill-switch.component';
import { InvoicesListComponent } from '../components/admin-page/components/invoices-list/invoices-list.component';
import { LicensesListComponent } from '../components/admin-page/components/licenses-list/licenses-list.component';
import { ApiCheckoutPageComponent } from '../components/api-checkout-page/api-checkout-page.component';
import { TenantApiInvoiceComponent } from '../components/admin-page/components/tenant-api-invoice/tenant-api-invoice.component';
import { CartComponent } from '../components/cart/cart.component';
import { EditApiSubscriptionComponent } from '../components/admin-page/components/edit-api-subscription/edit-api-subscription.component';
import { ApiInvoicesPageComponent } from '../components/api-invoices-page/api-invoices-page.component';
import { RegisterComponent } from '../components/register/register.component';
import { CookiePolicyComponent } from '../components/cookie-policy/cookie-policy.component';
import { ApiReceiptPageComponent } from '../components/api-receipt-page/api-receipt-page.component';
import { ApiJobLogsPageComponent } from '../components/api-job-logs-page/api-job-logs-page.component';
import { TenantApiJobLogsComponent } from '../components/admin-page/components/tenant-api-job-logs/tenant-api-job-logs.component';
import { LogSearchComponent } from '../components/admin-page/components/log-search/log-search.component';
import { superAdminGuard } from './auth-guards/super-admin.guard';
import { ProductSquashComponent } from '../components/admin-page/components/product-squash/product-squash.component';
import { FaqPageComponent } from '../components/faq-page/faq-page.component';
import { LoginSuccessComponent } from '../components/login-success/login-success.component';
import { ActivationKeysPageComponent } from '../components/admin-page/components/activation-keys-page/activation-keys-page.component';
import { RegistrationDbLogsPageComponent } from '../components/admin-page/components/registration-db-logs-page/registration-db-logs-page.component';
import { registrationDbAdminGuard } from './auth-guards/registration-db-admin.guard';
import { PricingDisplayComponent } from '../components/admin-page/components/pricing-display/pricing-display.component';
import { pricingGuard } from './auth-guards/pricing.guard';
import { BuyDesCreditsPageComponent } from '../components/buy-des-credits-page/buy-des-credits-page.component';
import { DesCreditsPageComponent } from '../components/des-credits-page/des-credits-page.component';
import { DesCheckoutPageComponent } from '../components/des-checkout-page/des-checkout-page.component';
import { DesReceiptPageComponent } from '../components/des-receipt-page/des-receipt-page.component';

const routes: Routes = [
  {
    path: '',
    component: DashboardComponent,
    canActivate: [userGuard],
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'login-success',
    component: LoginSuccessComponent,
  },
  {
    path: 'terms-and-conditions',
    component: TermsAndConditionsComponent,
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
  },
  {
    path: 'cookie-policy',
    component: CookiePolicyComponent,
  },
  {
    path: 'faq',
    component: FaqPageComponent,
  },
  {
    path: 'admin/pricing',
    canActivate: [userGuard, pricingGuard],
    component: PricingDisplayComponent,
  },
  {
    path: 'admin',
    canActivate: [userGuard, portalAdminGuard],
    children: [
      {
        path: 'activation-keys',
        canActivate: [activationKeysAdminGuard],
        component: ActivationKeysPageComponent,
      },
      {
        path: 'registration-db-logs',
        canActivate: [registrationDbAdminGuard],
        component: RegistrationDbLogsPageComponent,
      },
      {
        path: 'tenants',
        component: TenantsListComponent,
      },
      {
        path: 'create-tenant',
        component: CreateTenantComponent,
      },
      {
        path: 'tenant/:id',
        component: TenantDetailsComponent,
      },
      {
        path: 'tenant/:tenantId/invoice/:invoiceId',
        component: TenantInvoiceComponent,
      },
      {
        path: 'tenant/:tenantId/api-invoice/:invoiceId',
        component: TenantApiInvoiceComponent,
      },
      {
        path: 'tenant/:tenantId/api-job-logs',
        component: TenantApiJobLogsComponent,
      },
      {
        path: 'tenant/:tenantId/edit',
        component: TenantAdminEditComponent,
      },
      {
        path: 'invoices',
        component: InvoicesListComponent,
      },
      {
        path: 'licenses',
        component: LicensesListComponent,
      },
      {
        path: 'products',
        component: ProductListComponent,
      },
      {
        path: 'product/:id',
        component: ProductDetailsComponent,
      },
      {
        path: 'add-product',
        component: AddProductComponent,
      },
      {
        path: 'add-product-variant/:id',
        component: AddProductVariantComponent,
      },
      {
        path: 'edit-product/:id',
        component: EditProductComponent,
      },
      {
        path: 'edit-product-variant/:id',
        component: EditProductVariantComponent,
      },
      {
        path: 'edit-license/:id',
        component: EditLicenseComponent,
      },
      {
        path: 'license-change-history/:id',
        component: LicenseChangeHistoryComponent,
      },
      {
        path: 'edit-subscription/:id',
        component: EditApiSubscriptionComponent,
      },
      {
        path: 'franchises',
        component: FranchisesListComponent,
      },
      {
        path: 'franchise/:id',
        component: FranchiseDetailsComponent,
      },
      {
        path: 'kill-switch',
        component: KillSwitchComponent,
      },
      {
        path: 'log-search',
        component: LogSearchComponent,
      },
      {
        path: 'product-squash',
        canActivate: [superAdminGuard],
        component: ProductSquashComponent,
      },
    ],
  },
  {
    path: 'sign-up/:invitationId',
    component: SignUpComponent,
  },
  {
    path: 'register',
    component: RegisterComponent,
  },
  {
    path: 'manage-auth',
    component: ManageAuthComponent,
  },
  {
    path: 'user-settings',
    canActivate: [userGuard],
    component: UserSettingsComponent,
  },
  {
    path: 'edit-user/:id',
    canActivate: [userGuard],
    component: EditUserComponent,
  },
  {
    path: 'tenant',
    canActivate: [userGuard],
    children: [
      {
        path: 'settings',
        component: TenantSettingsComponent,
      },
      {
        path: 'setup-info',
        component: TenantInfoSetupFormComponent,
      },
      {
        path: 'update-info',
        component: TenantInfoPageComponent,
      },
      {
        path: 'users',
        component: TenantUsersComponent,
      },
    ],
  },
  {
    path: 'history',
    canActivate: [userGuard],
    component: HistoryComponent,
  },
  {
    path: 'licenses',
    canActivate: [userGuard],
    component: LicensesPageComponent,
  },
  {
    path: 'des-credits',
    canActivate: [userGuard],
    component: DesCreditsPageComponent,
  },
  {
    path: 'buy-des-credits',
    canActivate: [userGuard],
    component: BuyDesCreditsPageComponent,
  },
  {
    path: 'des-checkout/:id',
    canActivate: [userGuard],
    component: DesCheckoutPageComponent,
  },
  {
    path: 'des-receipt/:id',
    canActivate: [userGuard],
    component: DesReceiptPageComponent,
  },
  {
    path: 'receipts',
    canActivate: [userGuard],
    component: ReceiptsPageComponent,
  },
  {
    path: 'api-invoices',
    canActivate: [userGuard],
    component: ApiInvoicesPageComponent,
  },
  {
    path: 'api-job-logs',
    canActivate: [userGuard],
    component: ApiJobLogsPageComponent,
  },
  {
    path: 'cart',
    canActivate: [userGuard],
    component: CartComponent,
  },
  {
    path: 'checkout/:id',
    canActivate: [userGuard],
    component: CheckoutPageComponent,
  },
  {
    path: 'api-checkout/:id',
    canActivate: [userGuard],
    component: ApiCheckoutPageComponent,
  },
  {
    path: 'receipt/:id',
    canActivate: [userGuard],
    component: ReceiptPageComponent,
  },
  {
    path: 'api-receipt/:id',
    canActivate: [userGuard],
    component: ApiReceiptPageComponent,
  },
  {
    path: 'saved-payment-methods',
    canActivate: [userGuard],
    component: SavedPaymentMethodsComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
